import { runInAction, toJS } from 'mobx';

import { toISODateString } from '@/lib/date-utils';

import type {
  CalendarEventAttendee,
  Meeting,
  MeetingTab,
} from './meetings.types';
import type { Meeting as StoreMeeting, Attendee } from '@littlebird/sdk';

export function storeAttendeesToDisplayAttendees(
  attendees: StoreMeeting['attendees']
): Meeting['attendees'] {
  if (!attendees) return null;
  return attendees
    .filter((a: Attendee) => a.email !== null)
    .map((a: Attendee) => ({
      email: a.email as string,
      displayName: a.display_name ?? undefined,
      avatarUrl: a.avatar_url ?? undefined,
      responseStatus: a.response_status ?? undefined,
      organizer: a.organizer ?? undefined,
      self: a.self ?? undefined,
      resource: a.resource ?? undefined,
    }));
}

export function storeMeetingToMeeting(
  storeMeeting: StoreMeeting | null | undefined
): Meeting | undefined {
  if (!storeMeeting) {
    return undefined;
  }

  return {
    id: storeMeeting.id,
    name: storeMeeting.name,
    summary: storeMeeting.summary,
    tldr: storeMeeting.tldr,
    personified_transcript: storeMeeting.personifiedTranscript,
    notes: toJS(storeMeeting.notes) as Meeting['notes'],
    prep: storeMeeting.prep,
    source_app: storeMeeting.sourceApp,
    external_source: storeMeeting.externalSource,
    event_id: storeMeeting.eventId,
    event_start_at: toISODateString(storeMeeting.eventStartAt),
    project_id: storeMeeting.projectId,
    status: storeMeeting.status as Meeting['status'],
    summary_status: storeMeeting.summaryStatus as Meeting['summary_status'],
    attendees: storeAttendeesToDisplayAttendees(storeMeeting.attendees),
    speakers: storeMeeting.speakers,
    ended_at: toISODateString(storeMeeting.endedAt),
    first_chunk_timestamp: toISODateString(storeMeeting.firstChunkTimestamp),
    last_transcribed_at: toISODateString(storeMeeting.lastTranscribedAt),
    first_transcript_at: toISODateString(storeMeeting.firstTranscriptAt),
    transcript_deleted_at: toISODateString(storeMeeting.transcriptDeletedAt),
    created_at:
      toISODateString(storeMeeting.createdAt) ?? new Date().toISOString(),
    updated_at:
      toISODateString(storeMeeting.updatedAt) ?? new Date().toISOString(),
  };
}

export function convertDesktopAttendeesToStoreAttendees(
  attendees: Meeting['attendees'] | null | undefined
): StoreMeeting['attendees'] {
  if (!attendees) return null;
  return attendees.map((a) => ({
    email: a.email ?? null,
    display_name: a.displayName ?? null,
    avatar_url: a.avatarUrl ?? null,
    response_status: a.responseStatus ?? null,
    organizer: a.organizer ?? null,
    self: a.self ?? null,
    resource: a.resource ?? null,
    optional: null,
  }));
}

export function getDisplayAttendees(
  liveAttendees: CalendarEventAttendee[] | null | undefined,
  persistedAttendees: Meeting['attendees'] | null | undefined
): NonNullable<Meeting['attendees']> {
  return liveAttendees?.length ? liveAttendees : (persistedAttendees ?? []);
}

export function getOtherAttendees(
  attendees: CalendarEventAttendee[] | null | undefined,
  currentUserEmail: string | null | undefined
): CalendarEventAttendee[] {
  const email = currentUserEmail?.toLowerCase() ?? '';
  return (attendees ?? []).filter(
    (attendee) =>
      !attendee.self && (!email || attendee.email?.toLowerCase() !== email)
  );
}

/** Notes synced from another app have a summary and no transcript, so they open on the summary */
export function getDefaultMeetingTab(
  externalSource: string | null | undefined
): MeetingTab {
  return externalSource ? 'summary' : 'transcript';
}

export type SummaryViewState =
  | { type: 'summarizing' }
  | { type: 'meeting-in-progress' }
  | { type: 'no-transcript' }
  | { type: 'summary-available'; isResummarizing: boolean };

/**
 * Decides what the meeting Summary tab shows.
 *
 * A stored summary is shown whenever there is one, even under a status other
 * than `completed`: the backend's summarize activity sets `no_transcript`
 * without clearing `summary`, so keying only off the status would hide a
 * summary the user already had whenever a later re-summarize ran against a
 * transcript that had not landed yet.
 */
export function getSummaryViewState(
  meeting: Pick<Meeting, 'summary' | 'summary_status'>,
  isMeetingBeingRecorded: boolean
): SummaryViewState {
  const summaryStatus = meeting.summary_status;

  if (isMeetingBeingRecorded) {
    return { type: 'meeting-in-progress' };
  }

  if (summaryStatus === 'in_progress') {
    return { type: 'summarizing' };
  }

  if (summaryStatus === 'completed' || summaryStatus === 'resummarizing') {
    return {
      type: 'summary-available',
      isResummarizing: summaryStatus === 'resummarizing',
    };
  }

  if (meeting.summary?.trim()) {
    return { type: 'summary-available', isResummarizing: false };
  }

  return { type: 'no-transcript' };
}

export type MeetingRecordingSummaryDisposition =
  | 'auto_summarize_requested'
  | 'auto_summarize_word_threshold'
  | 'summary_deferred'
  | 'local_post_processing'
  | 'local_post_processing_start_failed'
  | 'skipped_contextkit_crash'
  | 'skipped_meeting_not_in_backend'
  | 'skipped_unknown';

export type MeetingRecordingStopTelemetryInput = {
  shouldFinalizeStoppedMeeting: boolean;
  meetingExistsInBackend: boolean;
  enteredFinalizePath: boolean;
  usesLocalPostProcessing: boolean;
  effectiveDeferSummary: boolean;
  localPostProcessingStartFailed: boolean;
  deferOverriddenByWordThreshold: boolean;
};

/**
 * Explains what a stop did about summarization, so PostHog can separate a
 * deliberate deferral from a stop that silently never asked the backend.
 *
 * Only stops of an active recording are modelled: the event is gated on a
 * recording `startedAt`, and that state always carries a transcript manager.
 */
export function resolveMeetingRecordingStoppedSummaryDisposition(
  input: MeetingRecordingStopTelemetryInput
): MeetingRecordingSummaryDisposition {
  if (!input.enteredFinalizePath) {
    if (!input.shouldFinalizeStoppedMeeting) {
      return 'skipped_contextkit_crash';
    }
    if (!input.meetingExistsInBackend) {
      return 'skipped_meeting_not_in_backend';
    }
    return 'skipped_unknown';
  }

  if (input.usesLocalPostProcessing) {
    if (input.localPostProcessingStartFailed) {
      return 'local_post_processing_start_failed';
    }
    if (input.effectiveDeferSummary) {
      return 'summary_deferred';
    }
    if (input.deferOverriddenByWordThreshold) {
      return 'auto_summarize_word_threshold';
    }
    return 'local_post_processing';
  }

  if (input.effectiveDeferSummary) {
    return 'summary_deferred';
  }
  return input.deferOverriddenByWordThreshold
    ? 'auto_summarize_word_threshold'
    : 'auto_summarize_requested';
}

export function updateStoreMeetingFromDesktopMeeting(
  storeMeeting: StoreMeeting,
  meeting: Partial<Meeting> & { id: string }
): void {
  const updateData: Record<string, unknown> = {};
  if (meeting.name !== undefined) updateData.name = meeting.name;
  if (meeting.summary !== undefined) updateData.summary = meeting.summary;
  if (meeting.tldr !== undefined) updateData.tldr = meeting.tldr;
  if (meeting.personified_transcript !== undefined)
    updateData.personifiedTranscript = meeting.personified_transcript;
  if (meeting.notes !== undefined) updateData.notes = meeting.notes;
  if (meeting.prep !== undefined) updateData.prep = meeting.prep;
  if (meeting.source_app !== undefined)
    updateData.sourceApp = meeting.source_app;
  if (meeting.event_id !== undefined) updateData.eventId = meeting.event_id;
  if (meeting.event_start_at !== undefined)
    updateData.eventStartAt = meeting.event_start_at
      ? new Date(meeting.event_start_at)
      : null;
  if (meeting.project_id !== undefined)
    updateData.projectId = meeting.project_id;
  if (meeting.status !== undefined) updateData.status = meeting.status;
  if (meeting.summary_status !== undefined)
    updateData.summaryStatus = meeting.summary_status;
  if (meeting.attendees !== undefined)
    updateData.attendees = convertDesktopAttendeesToStoreAttendees(
      meeting.attendees
    );
  if (meeting.speakers !== undefined) updateData.speakers = meeting.speakers;
  if (meeting.ended_at !== undefined)
    updateData.endedAt = meeting.ended_at ? new Date(meeting.ended_at) : null;
  if (meeting.first_chunk_timestamp !== undefined)
    updateData.firstChunkTimestamp = meeting.first_chunk_timestamp
      ? new Date(meeting.first_chunk_timestamp)
      : null;
  if (meeting.last_transcribed_at !== undefined)
    updateData.lastTranscribedAt = meeting.last_transcribed_at
      ? new Date(meeting.last_transcribed_at)
      : null;
  if (meeting.first_transcript_at !== undefined)
    updateData.firstTranscriptAt = meeting.first_transcript_at
      ? new Date(meeting.first_transcript_at)
      : null;
  if (meeting.transcript_deleted_at !== undefined)
    updateData.transcriptDeletedAt = meeting.transcript_deleted_at
      ? new Date(meeting.transcript_deleted_at)
      : null;

  runInAction(() => {
    Object.assign(storeMeeting, updateData);
  });
}
